import { useCallback, useEffect, useRef, useState } from "react";
import type { MasterListItem } from "../../apis";

/** An antd Select option built from a master-list item: the item's name and id. */
export interface MasterListOption {
  label: string;
  value: string;
}

/**
 * Append a free-text option for the current search term when it doesn't match any fetched option,
 * so a learner whose company / job title isn't in the master list can still submit what they typed.
 * The synthetic option's value === label === the term, so both id and name persist as that text.
 *
 * Shared by the job form and the off-campus request form, which both pick company and role this way.
 */
export const withCustomOption = (options: MasterListOption[], term: string): MasterListOption[] => {
  const q = term.trim();
  if (!q) return options;
  const exists = options.some(o => o.label.toLowerCase() === q.toLowerCase());
  return exists ? options : [...options, { label: q, value: q }];
};

/**
 * Server-side search for a master list (company / role). Loads an initial "popular" page on mount,
 * then debounces typed searches (≥ 2 chars) — mirrors bi-main's Experience-form behaviour. A request
 * sequence guard ignores stale/out-of-order responses (axios 0.21 has no AbortController support).
 * Returns antd Select-ready options.
 */
export const useMasterListSearch = (search: (query: string) => Promise<{ items: MasterListItem[] }>) => {
  const [options, setOptions] = useState<MasterListOption[]>([]);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const run = useCallback(
    async (query: string) => {
      seqRef.current += 1;
      const seq = seqRef.current;
      setLoading(true);
      try {
        const { items } = await search(query);
        if (seq !== seqRef.current) return; // a newer search superseded this one
        setOptions((items || []).map(item => ({ label: item.Name, value: item.Id })));
      } catch {
        // failed — keep the previous options
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    },
    [search]
  );

  // Initial popular load + clear any pending debounce on unmount.
  useEffect(() => {
    run("");
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [run]);

  const onSearch = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => run(query.trim().length >= 2 ? query : ""), 500);
    },
    [run]
  );

  return { options, loading, onSearch };
};
